import { Subject, take } from 'rxjs';
import { ClientMetricReportDirection, ClientMetricReportMediaType, } from 'amazon-chime-sdk-js';
import { streemLogger as _streemLogger } from './util/streem_logger';
import { getVideoMetricsForRemoteUser } from './util/get_video_metrics_for_remote_user';
export class RoomMonitor {
    constructor(attendeeId, connectionStateObservable, isLocalAudioTrackEnabledObservable, isLocalVideoTrackEnabledObservable, chimeObserver) {
        this.LOW_FRAME_RATE_THRESHOLD = 2;
        this.logger = _streemLogger.extend('sdk-chime-video:RoomMonitor');
        this.roomWebRtcStatsSubject = new Subject();
        this.roomWebRtcStats = this.roomWebRtcStatsSubject.asObservable();
        this.isLocalVideoFrozenSubject = new Subject();
        this.isLocalVideoFrozen$ = this.isLocalVideoFrozenSubject.asObservable();
        this.updateRoomStats = async (clientMetricReport) => {
            try {
                if (this.connectionState != 'CONNECTED') {
                    this.logger.debug('Skipping metrics as connection state is not CONNECTED:' + this.connectionState);
                    return;
                }
                if (!clientMetricReport) {
                    this.logger.warn('client metric report is not received/defined');
                    return;
                }
                // We're tracking the enabled state for the local audio and video tracks because it impacts the values of
                // downstream and upstream metrics
                this.isLocalAudioTrackEnabledSubscription = this.isLocalAudioTrackEnabledObservable
                    .pipe(take(1))
                    .subscribe(isLocalAudioTrackEnabled => {
                    this.isLocalAudioTrackEnabled = isLocalAudioTrackEnabled;
                });
                this.isLocalVideoTrackEnabledSubscription = this.isLocalVideoTrackEnabledObservable
                    .pipe(take(1))
                    .subscribe(isLocalVideoTrackEnabled => {
                    this.isLocalVideoTrackEnabled = isLocalVideoTrackEnabled;
                });
                const webRtcStatsReport = this.buildWebRtcStatsReport(clientMetricReport);
                this.roomWebRtcStatsSubject.next(webRtcStatsReport);
                this.logger.debug(`webRtcStatsReport:${JSON.stringify(webRtcStatsReport)}`);
                const isCurrentLocalVideoFrozen = this.isVideoFrozen(clientMetricReport);
                this.isLocalVideoFrozenSubject.next(isCurrentLocalVideoFrozen);
            }
            catch (e) {
                this.logger.error('Room monitor could not get room stats: ', e);
            }
        };
        this.attendeeId = attendeeId;
        this.connectionStateSubscription = connectionStateObservable
            .pipe(take(1))
            .subscribe(state => {
            this.connectionState = state;
        });
        this.isLocalAudioTrackEnabledObservable = isLocalAudioTrackEnabledObservable;
        this.isLocalVideoTrackEnabledObservable = isLocalVideoTrackEnabledObservable;
        this.chimeObserver = chimeObserver;
        this.chimeObserver.addMetricsObserver(this.updateRoomStats);
        this.logger.info('Room Monitor connected');
    }
    disconnect() {
        this.connectionStateSubscription.unsubscribe();
        this.isLocalAudioTrackEnabledSubscription.unsubscribe();
        this.isLocalVideoTrackEnabledSubscription.unsubscribe();
        this.chimeObserver.removeMetricsObserver(this.updateRoomStats);
        this.logger.info('Room monitor disconnected');
    }
    buildWebRtcStatsReport(clientMetricReport) {
        const localAudioTrackStats = [];
        const localVideoTrackStats = [];
        const remoteAudioTrackStats = [];
        const remoteVideoTrackStats = [];
        let peerConnectionId = '';
        clientMetricReport.getRTCStatsReport().forEach(stats => {
            if (stats.type === 'peer-connection') {
                peerConnectionId = stats.id;
            }
        });
        if (Object.keys(clientMetricReport.streamMetricReports).length > 0) {
            const observableMetrics = clientMetricReport.getObservableMetrics();
            const observableVideoMetrics = clientMetricReport.getObservableVideoMetrics();
            if (observableMetrics && Object.keys(observableMetrics).length > 0) {
                localAudioTrackStats.push(this.buildLocalAudioTrackStats(clientMetricReport, observableMetrics));
                remoteAudioTrackStats.push(this.buildRemoteAudioTrackStats(clientMetricReport, observableMetrics));
            }
            if (observableVideoMetrics && Object.keys(observableVideoMetrics).length > 0) {
                const localVideoTrackWebRtcStats = this.buildLocalVideoTrackStats(clientMetricReport, observableVideoMetrics, this.attendeeId);
                if (localVideoTrackWebRtcStats) {
                    localVideoTrackStats.push(localVideoTrackWebRtcStats);
                }
                const remoteVideoTrackWebRtcStats = this.buildRemoteVideoTrackStats(clientMetricReport, observableVideoMetrics, this.attendeeId, this.logger);
                if (remoteVideoTrackWebRtcStats) {
                    remoteVideoTrackStats.push(remoteVideoTrackWebRtcStats);
                }
            }
        }
        return {
            peerConnectionId,
            localAudioTrackStats,
            localVideoTrackStats,
            remoteAudioTrackStats,
            remoteVideoTrackStats,
        };
    }
    /* LocalAudioTrackStats
     * -- audioLevel: number | null;
     * -- bytesSent: number | null;
     * -- bitRate: number | null;
     * -- packetsSent: number | null;
     * -- roundTripTime: number | null;
     * -- jitter: number | null;
     * -- timestamp: number;
     * -- packetsLost: number | null;
     * -- packetLossPercentage: number | null;
     * -- codec: string | null;
     * -- ssrc: string;
     * -- enabled?: boolean;
     * -- trackId: string; // Might not be available in Chime
     * -- trackSid: string; // Might not be available in Chime
     */
    buildLocalAudioTrackStats(clientMetricReport, observableMetrics) {
        return {
            audioLevel: observableMetrics.audioUpstreamLevel,
            bytesSent: this.getTrackBytesTransmitted(clientMetricReport, ClientMetricReportMediaType.AUDIO, ClientMetricReportDirection.UPSTREAM),
            bitRate: this.getAudioTrackBitRate(clientMetricReport, ClientMetricReportDirection.UPSTREAM),
            packetsSent: observableMetrics.audioPacketsSent,
            roundTripTime: observableMetrics.audioUpstreamRoundTripTimeMs,
            jitter: observableMetrics.audioUpstreamJitterMs,
            timestamp: clientMetricReport.currentTimestampMs,
            packetsLost: this.getTrackPacketsLost(clientMetricReport, ClientMetricReportMediaType.AUDIO, ClientMetricReportDirection.UPSTREAM),
            packetLossPercentage: observableMetrics.audioPacketLossPercent,
            codec: this.getCodecOrDefault(clientMetricReport.getRTCStatsReport()).audioCodec,
            ssrc: this.getStreamSsrc(clientMetricReport.streamMetricReports, ClientMetricReportMediaType.AUDIO, ClientMetricReportDirection.UPSTREAM),
            enabled: this.isLocalAudioTrackEnabled,
            trackId: '',
            trackSid: '',
        };
    }
    /* LocalVideoTrackStats
     * -- captureDimensions: VideoTrackDimensions | null;
     * -- captureFrameRate: number | null;
     * -- dimensions: VideoTrackDimensions | null;
     * -- frameRate: number | null;
     * -- bytesSent: number | null;
     * -- bitRate: number | null;
     * -- packetsSent: number | null;
     * -- roundTripTime: number | null;
     * -- jitter: number | null;
     * -- timestamp: number;
     * -- ssrc: string;
     * -- packetsLost: number | null;
     * -- packetLossPercentage: number | null;
     * -- codec: string | null;
     * -- enabled?: boolean;
     * -- trackId: string;  // Might not be available in Chime
     * -- trackSid: string;  // Might not be available in Chime
     */
    buildLocalVideoTrackStats(clientMetricReport, observableVideoMetrics, attendeeId) {
        const videoUpstreamSsrc = this.getStreamSsrc(clientMetricReport.streamMetricReports, ClientMetricReportMediaType.VIDEO, ClientMetricReportDirection.UPSTREAM);
        if (!videoUpstreamSsrc) {
            return null;
        }
        const upstreamObservableVideoMetrics = observableVideoMetrics[attendeeId][videoUpstreamSsrc];
        if (!upstreamObservableVideoMetrics) {
            return null;
        }
        const videoSourceStats = this.parseWebRtcStatsReportForVideoSourceStats(clientMetricReport.getRTCStatsReport());
        return {
            captureDimensions: {
                width: videoSourceStats?.width ?? null,
                height: videoSourceStats?.height ?? null,
            },
            captureFrameRate: videoSourceStats?.framesPerSecond ?? null,
            dimensions: {
                width: upstreamObservableVideoMetrics.videoUpstreamFrameWidth,
                height: upstreamObservableVideoMetrics.videoUpstreamFrameHeight,
            },
            frameRate: upstreamObservableVideoMetrics.videoUpstreamFramesEncodedPerSecond,
            bytesSent: this.getTrackBytesTransmitted(clientMetricReport, ClientMetricReportMediaType.VIDEO, ClientMetricReportDirection.UPSTREAM),
            bitRate: upstreamObservableVideoMetrics.videoUpstreamBitrate,
            packetsSent: upstreamObservableVideoMetrics.videoUpstreamPacketsSent,
            roundTripTime: upstreamObservableVideoMetrics.videoUpstreamRoundTripTimeMs,
            jitter: upstreamObservableVideoMetrics.videoUpstreamJitterMs,
            timestamp: clientMetricReport.currentTimestampMs,
            packetsLost: this.getTrackPacketsLost(clientMetricReport, ClientMetricReportMediaType.VIDEO, ClientMetricReportDirection.UPSTREAM),
            packetLossPercentage: upstreamObservableVideoMetrics.videoUpstreamPacketLossPercent,
            codec: this.getCodecOrDefault(clientMetricReport.getRTCStatsReport()).videoCodec,
            ssrc: videoUpstreamSsrc,
            enabled: this.isLocalVideoTrackEnabled,
            trackId: '',
            trackSid: '',
        };
    }
    /* RemoteAudioTrackStats
     * -- audioLevel: number | null;
     * -- bytesReceived: number | null;
     * -- bitRate: number | null;
     * -- packetsReceived: number | null;
     * -- jitter: number | null;
     * -- timestamp: number;
     * -- packetsLost: number | null;
     * -- packetLossPercentage: number | null;
     * -- codec: string | null;
     * -- ssrc: string;
     * -- trackId: string;  // Might not be available in Chime
     * -- trackSid: string;  // Might not be available in Chime
     */
    buildRemoteAudioTrackStats(clientMetricReport, observableMetrics) {
        return {
            audioLevel: observableMetrics.audioDownstreamLevel,
            bytesReceived: this.getTrackBytesTransmitted(clientMetricReport, ClientMetricReportMediaType.AUDIO, ClientMetricReportDirection.DOWNSTREAM),
            bitRate: this.getAudioTrackBitRate(clientMetricReport, ClientMetricReportDirection.DOWNSTREAM),
            packetsReceived: observableMetrics.audioPacketsReceived,
            jitter: observableMetrics.audioDownstreamJitterMs,
            timestamp: clientMetricReport.currentTimestampMs,
            packetsLost: this.getTrackPacketsLost(clientMetricReport, ClientMetricReportMediaType.AUDIO, ClientMetricReportDirection.DOWNSTREAM),
            packetLossPercentage: observableMetrics.audioPacketLossPercent,
            codec: this.getCodecOrDefault(clientMetricReport.getRTCStatsReport()).audioCodec,
            ssrc: this.getStreamSsrc(clientMetricReport.streamMetricReports, ClientMetricReportMediaType.AUDIO, ClientMetricReportDirection.DOWNSTREAM),
            trackId: '',
            trackSid: '',
        };
    }
    /* RemoteVideoTrackStats:
     * -- dimensions: VideoTrackDimensions | null;
     * -- frameRate: number | null;
     * -- bytesReceived: number | null;
     * -- bitRate: number | null;
     * -- packetsReceived: number | null;
     * -- timestamp: number;
     * -- packetsLost: number | null;
     * -- packetLossPercentage: number | null;
     * -- codec: string | null;
     * -- ssrc: string;
     * -- trackId: string;  // Might not be available in Chime
     * -- trackSid: string;  // Might not be available in Chime
     * -- jitter: number | null;
     */
    buildRemoteVideoTrackStats(clientMetricReport, observableVideoMetrics, attendeeId, logger) {
        const downstreamObservableVideoMetrics = getVideoMetricsForRemoteUser(observableVideoMetrics, attendeeId, logger);
        if (!downstreamObservableVideoMetrics) {
            return null;
        }
        return {
            dimensions: {
                width: downstreamObservableVideoMetrics.videoDownstreamFrameWidth,
                height: downstreamObservableVideoMetrics.videoDownstreamFrameHeight,
            },
            frameRate: downstreamObservableVideoMetrics.videoDownstreamFramesDecodedPerSecond,
            bytesReceived: this.getTrackBytesTransmitted(clientMetricReport, ClientMetricReportMediaType.VIDEO, ClientMetricReportDirection.DOWNSTREAM),
            bitRate: downstreamObservableVideoMetrics.videoDownstreamBitrate,
            packetsReceived: downstreamObservableVideoMetrics.videoDownstreamPacketsReceived,
            timestamp: clientMetricReport.currentTimestampMs,
            packetsLost: this.getTrackPacketsLost(clientMetricReport, ClientMetricReportMediaType.VIDEO, ClientMetricReportDirection.DOWNSTREAM),
            packetLossPercentage: downstreamObservableVideoMetrics.videoDownstreamPacketLossPercent,
            codec: this.getCodecOrDefault(clientMetricReport.getRTCStatsReport()).videoCodec,
            ssrc: this.getStreamSsrc(clientMetricReport.streamMetricReports, ClientMetricReportMediaType.VIDEO, ClientMetricReportDirection.DOWNSTREAM),
            trackId: '',
            trackSid: '',
            jitter: downstreamObservableVideoMetrics.videoDownstreamJitterMs,
            jitterBufferDelay: downstreamObservableVideoMetrics.videoDownstreamJitterBufferDelay ?? null,
        };
    }
    parseWebRtcStatsReportForVideoSourceStats(rtcStatsReport) {
        if (!rtcStatsReport) {
            return null;
        }
        let videoSourceStats = null;
        rtcStatsReport.forEach(stats => {
            if (stats.type === 'media-source' && stats.kind === 'video') {
                videoSourceStats = {
                    height: stats.height,
                    width: stats.width,
                    framesPerSecond: stats.framesPerSecond,
                };
            }
        });
        return videoSourceStats;
    }
    getTrackPacketsLost(clientMetricReport, mediaType, direction) {
        const streamMetricReport = Object.values(clientMetricReport.streamMetricReports).find((stream) => stream.direction === direction && stream.mediaType === mediaType);
        if (!streamMetricReport) {
            return null;
        }
        return streamMetricReport.currentMetrics.packetsLost;
    }
    getTrackBytesTransmitted(clientMetricReport, mediaType, direction) {
        const streamMetricReport = Object.values(clientMetricReport.streamMetricReports).find((stream) => stream.direction === direction && stream.mediaType === mediaType);
        if (!streamMetricReport) {
            return null;
        }
        return direction === ClientMetricReportDirection.UPSTREAM
            ? streamMetricReport.currentMetrics.bytesSent
            : streamMetricReport.currentMetrics.bytesReceived;
    }
    getAudioTrackBitRate(clientMetricReport, direction) {
        const audioStreamMetricReport = Object.values(clientMetricReport.streamMetricReports).find((stream) => stream.direction === direction &&
            stream.mediaType === ClientMetricReportMediaType.AUDIO);
        if (!audioStreamMetricReport) {
            return null;
        }
        const currentBytes = direction === ClientMetricReportDirection.UPSTREAM
            ? audioStreamMetricReport.currentMetrics.bytesSent
            : audioStreamMetricReport.currentMetrics.bytesReceived;
        const previousBytes = direction === ClientMetricReportDirection.UPSTREAM
            ? audioStreamMetricReport.previousMetrics.bytesSent
            : audioStreamMetricReport.previousMetrics.bytesReceived;
        return this.calculateBitrate(currentBytes, previousBytes);
    }
    calculateBitrate(currentBytes, previousBytes) {
        const bytesPerSecond = currentBytes - previousBytes;
        return bytesPerSecond * 8; // Convert to bits per second
    }
    getCodecOrDefault(rtcStatsReport) {
        let audioCodec = 'unknown';
        let videoCodec = 'unknown';
        if (!rtcStatsReport)
            return { audioCodec, videoCodec };
        rtcStatsReport.forEach(stats => {
            if (stats.type === 'codec') {
                const mimeType = stats.mimeType.toLowerCase();
                if (mimeType.includes('audio')) {
                    audioCodec = mimeType.split('/')[1];
                }
                else if (mimeType.includes('video')) {
                    videoCodec = mimeType.split('/')[1];
                }
            }
        });
        return { audioCodec, videoCodec };
    }
    getStreamSsrc(streamMetricReports, mediaType, direction) {
        for (const ssrc in streamMetricReports) {
            if (streamMetricReports[ssrc].mediaType === mediaType &&
                streamMetricReports[ssrc].direction === direction) {
                return ssrc;
            }
        }
        return '';
    }
    isVideoFrozen(clientMetricReport) {
        const localVideoStreamMetricReport = Object.values(clientMetricReport.streamMetricReports).find((stream) => stream.direction === ClientMetricReportDirection.UPSTREAM &&
            stream.mediaType === ClientMetricReportMediaType.VIDEO);
        if (!localVideoStreamMetricReport)
            return false;
        return (localVideoStreamMetricReport.currentMetrics.bytesSent > 0 &&
            localVideoStreamMetricReport.currentMetrics.frameRate < this.LOW_FRAME_RATE_THRESHOLD);
    }
}
//# sourceMappingURL=room_monitor.js.map